'use client'

import React, { createContext, useContext, ReactNode, useEffect, useState } from 'react'
import { TFunction } from 'i18next'
import initTranslations from 'app/i18n'
import { useSession } from './SessionProvider'
import { useConfig } from './ConfigProvider'

/**
 * Translation Context
 *
 * Provides translation function (t) to client components without prop drilling.
 * Translations are initialized once based on the language from SessionProvider.
 */
interface TranslationContextValue {
    t: TFunction | null
    isLoading: boolean
    lang: string
}

const TranslationContext = createContext<TranslationContextValue>({
    t: null,
    isLoading: true,
    lang: 'en'
})

export const useTranslation = () => useContext(TranslationContext)

interface TranslationProviderProps {
    children: ReactNode
}

/**
 * TranslationProvider - Client Component
 *
 * Provides translation function to client components via React Context.
 *
 * Architecture:
 * - Consumes `lang` from SessionProvider (no need to pass it separately)
 * - Initializes translations once when lang changes
 * - Provides `t` function via context to all child components
 * - Handles loading state during translation initialization
 *
 * Usage:
 * ```tsx
 * const MyComponent = () => {
 *   const { t, isLoading } = useTranslation()
 *   if (isLoading) return <Loading />
 *   return <div>{t('myKey')}</div>
 * }
 * ```
 */
export function TranslationProvider({ children }: TranslationProviderProps): React.JSX.Element {
    const { lang } = useSession()
    const sportType = useConfig()?.sport_type
    const [t, setT] = useState<TFunction | null>(null)
    const [isLoading, setIsLoading] = useState(true)

    useEffect(() => {
        const loadTranslations = async () => {
            if (!lang) {
                setIsLoading(false)
                return
            }

            try {
                setIsLoading(true)
                const { t: translator } = await initTranslations(lang, undefined, undefined, sportType)
                setT(() => translator)
            } catch (error) {
                console.error('[TranslationProvider] Failed to load translations:', error)
                // Fallback function that returns the key if translation fails
                setT(() => ((key: string) => key) as TFunction)
            } finally {
                setIsLoading(false)
            }
        }

        loadTranslations()
    }, [lang, sportType])

    return <TranslationContext.Provider value={{ t, isLoading, lang }}>{children}</TranslationContext.Provider>
}
